import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type AnalyticsReferenceDateMode = 'today' | 'historical';

export interface AnalyticsReferenceDateContextValue {
  mode: AnalyticsReferenceDateMode;
  asOfDate: string | null;
  enabled: boolean;
  setAsOfDate(date: string | null): void;
  resetToToday(): void;
}

interface StoredAnalyticsReferenceDateSettings {
  enabled: boolean;
  asOfDate: string | null;
}

export const ANALYTICS_REFERENCE_DATE_STORAGE_KEY =
  'kalpa-analytics-reference-date-settings';

const DEFAULT_SETTINGS: StoredAnalyticsReferenceDateSettings = {
  enabled: false,
  asOfDate: null,
};

export const isValidAnalyticsReferenceDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
};

export const todayLocalYmd = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`;
};

const dateFromYmd = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatAnalyticsReferenceDateLong = (value: string | null): string => {
  if (!isValidAnalyticsReferenceDate(value)) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(dateFromYmd(value));
};

export const formatAnalyticsReferenceDateCompact = (value: string | null): string => {
  if (!isValidAnalyticsReferenceDate(value)) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateFromYmd(value));
};

export const getAnalyticsReferenceDateParams = (
  enabled: boolean,
  asOfDate: string | null,
): { as_of_date?: string } => {
  if (!enabled || !isValidAnalyticsReferenceDate(asOfDate)) {
    return {};
  }
  return { as_of_date: asOfDate };
};

const loadSettings = (): StoredAnalyticsReferenceDateSettings => {
  try {
    const raw = localStorage.getItem(ANALYTICS_REFERENCE_DATE_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;

    const parsed = JSON.parse(raw) as Partial<StoredAnalyticsReferenceDateSettings>;
    if (parsed.enabled === true && isValidAnalyticsReferenceDate(parsed.asOfDate)) {
      return { enabled: true, asOfDate: parsed.asOfDate };
    }
  } catch {
    // Invalid or inaccessible localStorage should never break app startup.
  }
  return DEFAULT_SETTINGS;
};

const AnalyticsReferenceDateContext =
  createContext<AnalyticsReferenceDateContextValue | undefined>(undefined);

export const AnalyticsReferenceDateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] =
    useState<StoredAnalyticsReferenceDateSettings>(loadSettings);

  useEffect(() => {
    try {
      localStorage.setItem(ANALYTICS_REFERENCE_DATE_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [settings]);

  const value = useMemo<AnalyticsReferenceDateContextValue>(
    () => ({
      mode: settings.enabled ? 'historical' : 'today',
      asOfDate: settings.enabled ? settings.asOfDate : null,
      enabled: settings.enabled,
      setAsOfDate: (date: string | null) => {
        if (isValidAnalyticsReferenceDate(date)) {
          setSettings({ enabled: true, asOfDate: date });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      },
      resetToToday: () => setSettings(DEFAULT_SETTINGS),
    }),
    [settings],
  );

  return (
    <AnalyticsReferenceDateContext.Provider value={value}>
      {children}
    </AnalyticsReferenceDateContext.Provider>
  );
};

export const useAnalyticsReferenceDate = (): AnalyticsReferenceDateContextValue => {
  const context = useContext(AnalyticsReferenceDateContext);
  if (!context) {
    throw new Error(
      'useAnalyticsReferenceDate must be used within an AnalyticsReferenceDateProvider',
    );
  }
  return context;
};
